import i18n from "i18next";
import type { TFunction } from "i18next";
import { initReactI18next } from "react-i18next";

import { loadLanguage, saveLanguage } from "src/utils/languageStorage";

import baseLanguages from "./baseLanguages.json";
import en from "./locales/en/translation.json";
import fr from "./locales/fr/translation.json";
import es from "./locales/es/translation.json";
import de from "./locales/de/translation.json";
import pt from "./locales/pt/translation.json";
import zh from "./locales/zh/translation.json";
import sw from "./locales/sw/translation.json";
import ny from "./locales/ny/translation.json";

/**
 * Languages the UI is translated into. `nativeName` is what the user sees in
 * the language selector — each name is written in its own language.
 */
export const SUPPORTED_LANGUAGES = [
  { code: "en", nativeName: "English" },
  { code: "fr", nativeName: "Français" },
  { code: "es", nativeName: "Español" },
  { code: "de", nativeName: "Deutsch" },
  { code: "pt", nativeName: "Português" },
  { code: "zh", nativeName: "中文" },
  { code: "sw", nativeName: "Kiswahili" },
  { code: "ny", nativeName: "Chichewa" }
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["code"];

export const DEFAULT_LANGUAGE: LanguageCode = "en";

const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map((l) => l.code) as readonly string[];

export function isSupportedLanguage(code: string): code is LanguageCode {
  return SUPPORTED_CODES.includes(code);
}

/**
 * Languages that ship unconditionally (no feature flag). Every other language
 * in SUPPORTED_LANGUAGES is released incrementally via its `lang-<code>` flag —
 * see {@link languageFlagKey} and the `useEnabledLanguages` hook. English is
 * always included so there is always a working fallback.
 *
 * Sourced from baseLanguages.json so the (plain-JS) e2e suite can import the
 * same list — keeping the two in lock-step when a language is promoted. Each
 * entry is validated against SUPPORTED_LANGUAGES so a typo (e.g. "en-US") fails
 * loudly at startup instead of silently disabling a base language.
 */
export const BASE_LANGUAGE_CODES: readonly LanguageCode[] = (baseLanguages as string[]).filter(isSupportedLanguage);

if (BASE_LANGUAGE_CODES.length !== (baseLanguages as string[]).length) {
  throw new Error("baseLanguages.json contains a code that is not in SUPPORTED_LANGUAGES");
}

// All locale bundles are imported eagerly. The total translated string volume
// is small, so the cost is negligible today. If locale payloads grow, lazy-load
// non-base locales on demand in changeLanguage — tracked in issue #26.
const resources = {
  en: { translation: en },
  fr: { translation: fr },
  es: { translation: es },
  de: { translation: de },
  pt: { translation: pt },
  zh: { translation: zh },
  sw: { translation: sw },
  ny: { translation: ny }
} as const;

/**
 * Resolves the UI language, considering only the `allowed` set (defaults to the
 * always-on base languages, since feature flags aren't available synchronously
 * at startup — `useEnabledLanguages` refines this once flags resolve):
 *   1. an explicitly stored choice (`bao_settings.language`) — honoured if
 *      allowed, otherwise the default; a stored value is never auto-detected over
 *   2. the browser's preferred language (matched on its primary subtag), if allowed
 *   3. the default (English)
 */
export function detectInitialLanguage(allowed: readonly string[] = BASE_LANGUAGE_CODES): LanguageCode {
  const isAllowed = (code: string): code is LanguageCode => isSupportedLanguage(code) && allowed.includes(code);

  // A stored value signals explicit user intent: honour it if allowed, otherwise
  // fall back to the default — never auto-detect over it. (Matches LanguageGate
  // and avoids a base-language → English flash for a user with a stored,
  // not-yet-released language whose browser prefers a base language.)
  const stored = loadLanguage();
  if (stored) {
    return isAllowed(stored) ? stored : DEFAULT_LANGUAGE;
  }

  // `navigator.languages` can be an empty (non-null) array in some embedded
  // WebViews/desktop environments, so `??` is not enough — fall back to the
  // singular `navigator.language` whenever the list is empty.
  const navigatorLanguages: readonly string[] =
    typeof navigator !== "undefined"
      ? (navigator.languages?.length ? navigator.languages : [navigator.language]).filter(Boolean)
      : [];

  for (const lang of navigatorLanguages) {
    const primary = lang.toLowerCase().split("-")[0];
    if (isAllowed(primary)) {
      return primary;
    }
  }

  return DEFAULT_LANGUAGE;
}

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: detectInitialLanguage(),
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_CODES as string[],
    interpolation: {
      escapeValue: false // React already escapes
    },
    returnNull: false
  })
  .catch((error) => {
    // With static bundled resources this should never reject, but surface it
    // rather than swallowing a real failure.
    console.error("i18next initialisation failed", error);
  });

/**
 * Changes the active language, persisting the choice by default so it survives
 * reloads. Pass `{ persist: false }` for programmatic clamping (e.g. LanguageGate
 * reconciling against feature flags) that must not overwrite the user's stored
 * preference. Returns i18next's promise so callers can await translation load.
 */
export function changeLanguage(code: LanguageCode, options: { persist?: boolean } = {}): Promise<TFunction> {
  const { persist = true } = options;
  if (persist) {
    saveLanguage(code);
  }
  return i18n.changeLanguage(code);
}

export default i18n;
